import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import type {
    CategoryAssistPlan,
    CategorySuggestion,
    SubcategoryGenerationResult,
    SubcategoryTarget,
    TaxonomySnapshotNode,
} from '@/shared/assist/CategoryAssistContract';
import { UsageCategoryTaxonomyService } from './UsageCategoryTaxonomyService';
import { ICategorySuggestionEngineToken, type ICategorySuggestionEngine } from './CategorySuggestionEngine';
import { DirectoryApiService, type DirectoryUserRecord } from '../directory/DirectoryApiService';
import { extractDimension, DEFAULT_CLASSIFICATION_LEVELS } from './ClassificationDimensions';
import { DEFAULT_TAXONOMY, GENERAL_DEPARTMENT, type SeedNode } from './UsageCategoryDefaults';
import type { TaxonomyDraftNode, TaxonomyNodeInput, TaxonomyRowView, TaxonomyStats, UsageTaxonomyNode } from './UsageCategoryTaxonomyTypes';
import { canAddBeneathDepth } from './UsageCategoryTaxonomyTypes';

/**
 * Drives the Usage Categories page: loads the tenant's task-categorization
 * taxonomy from the context service into a `persisted` baseline + an editable
 * `draft`, runs all edits locally (no I/O) so the user can Save the diff or
 * Revert, and stages AI suggestions as further draft mutations for review.
 */
@injectable()
export class UsageCategoriesViewModel extends BaseViewModel {
    loading = true;
    saving = false;
    loadError: string | null = null;
    saveError: string | null = null;

    dimensionId: string | null = null;
    selectedCategoryDraftId: string | null = null;
    filterText = '';

    generating = false;
    assistError: string | null = null;
    assistNarrative = '';
    suggestions: CategorySuggestion[] = [];
    hasGenerated = false;

    assistModalDraftId: string | null = null;
    selectedSuggestionIndices = new Set<number>();

    private persisted: TaxonomyDraftNode[] = [];
    private draft: TaxonomyDraftNode[] = [];
    private removedServerIds = new Set<string>();
    private expanded = new Set<string>();
    private draftSeq = 0;

    generationPhase: 'idle' | 'building' | 'generating' = 'idle';
    generationProgress: { done: number; total: number } | null = null;
    generationError: string | null = null;
    generationResult: string | null = null;
    /** Whether generation/default-seed appends the fixed "General" catch-all. */
    includeGeneral = true;
    /** Fixed to a single grouping level (department): keeps the tree within the
     * three-level cap and removes the need for a user-facing "group by" picker. */
    classificationLevels: string[] = [...DEFAULT_CLASSIFICATION_LEVELS];
    private initializePromise: Promise<void> | null = null;

    constructor(
        private taxonomy: UsageCategoryTaxonomyService,
        @inject(ICategorySuggestionEngineToken) private engine: ICategorySuggestionEngine,
        private directory: DirectoryApiService,
    ) {
        super();
    }

    get notSeeded(): boolean {
        return !this.loading && this.loadError === null && this.dimensionId === null;
    }

    get categories(): TaxonomyDraftNode[] {
        return this.draft;
    }

    get visibleRows(): TaxonomyRowView[] {
        const filter = this.filterText.trim().toLowerCase();
        const rows: TaxonomyRowView[] = [];
        const walk = (nodes: TaxonomyDraftNode[], depth: number, parentDraftId: string | null): void => {
            for (const node of nodes) {
                if (filter.length > 0 && !subtreeMatches(node, filter)) continue;
                const hasChildren = node.children.length > 0;
                const expanded = filter.length > 0 ? hasChildren : this.expanded.has(node.draftId);
                rows.push({ node, depth, hasChildren, expanded, parentDraftId });
                if (hasChildren && expanded) walk(node.children, depth + 1, node.draftId);
            }
        };
        walk(this.draft, 0, null);
        return rows;
    }

    get isFiltering(): boolean {
        return this.filterText.trim().length > 0;
    }

    get matchCount(): number {
        const filter = this.filterText.trim().toLowerCase();
        if (filter.length === 0) return 0;
        let count = 0;
        const walk = (nodes: TaxonomyDraftNode[]): void => {
            for (const node of nodes) {
                if (node.label.toLowerCase().includes(filter)) count += 1;
                walk(node.children);
            }
        };
        walk(this.draft);
        return count;
    }

    setFilter(value: string): void {
        this.filterText = value;
        this.notifyChange();
    }

    isExpanded(draftId: string): boolean {
        return this.expanded.has(draftId);
    }

    setExpanded(draftId: string, value: boolean): void {
        if (value) this.expanded.add(draftId);
        else this.expanded.delete(draftId);
        this.notifyChange();
    }

    toggleExpand(draftId: string): void {
        this.setExpanded(draftId, !this.expanded.has(draftId));
    }

    expandAll(): void {
        const walk = (nodes: TaxonomyDraftNode[]): void => {
            for (const node of nodes) {
                if (node.children.length > 0) {
                    this.expanded.add(node.draftId);
                    walk(node.children);
                }
            }
        };
        walk(this.draft);
        this.notifyChange();
    }

    collapseAll(): void {
        this.expanded.clear();
        this.notifyChange();
    }

    get selectedCategory(): TaxonomyDraftNode | null {
        if (!this.selectedCategoryDraftId) return null;
        return this.findByDraftId(this.selectedCategoryDraftId);
    }

    get isDirty(): boolean {
        return this.removedServerIds.size > 0 || this.draft.some(hasPendingChange);
    }

    get stats(): TaxonomyStats {
        const subcategories = this.draft.reduce((sum, c) => sum + c.children.length, 0);
        return {
            categories: this.draft.length,
            subcategories,
            disabled: countDisabled(this.draft),
            pendingChanges: this.pendingChangeCount,
        };
    }

    get pendingChangeCount(): number {
        return this.removedServerIds.size + countPending(this.draft);
    }

    get canGenerate(): boolean {
        return this.dimensionId !== null && !this.generating;
    }

    get canApplySuggestions(): boolean {
        return this.selectedSuggestionIndices.size > 0;
    }

    async initialize(): Promise<void> {
        if (this.initializePromise !== null) return this.initializePromise;
        this.initializePromise = this.runInitialize();
        try {
            await this.initializePromise;
        } finally {
            this.initializePromise = null;
        }
    }

    private async runInitialize(): Promise<void> {
        this.loading = true;
        this.loadError = null;
        this.notifyChange();
        try {
            const result = await this.taxonomy.loadTaxonomy();
            this.dimensionId = result.dimensionId;
            this.persisted = result.nodes.map((n) => toDraft(n));
            this.draft = this.persisted.map(cloneDraft);
            this.removedServerIds.clear();
            this.resetSuggestions();
            if (!this.selectedCategoryDraftId || !this.draft.some((c) => c.draftId === this.selectedCategoryDraftId)) {
                this.selectedCategoryDraftId = this.draft[0]?.draftId ?? null;
            }
        } catch (cause) {
            this.loadError = cause instanceof Error ? cause.message : 'Failed to load usage categories';
            this.persisted = [];
            this.draft = [];
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    selectCategory(draftId: string): void {
        this.selectedCategoryDraftId = draftId;
        this.notifyChange();
    }

    addCategory(label: string): string {
        const node = this.newNode(label, this.draft.length);
        this.draft = [...this.draft, node];
        this.selectedCategoryDraftId = node.draftId;
        this.notifyChange();
        return node.draftId;
    }

    addSubcategory(parentDraftId: string, label: string): string | null {
        const parent = this.findByDraftId(parentDraftId);
        if (!parent) return null;
        if (!canAddBeneathDepth(this.nodeDepth(parentDraftId))) return null;
        const node = this.newNode(label, parent.children.length);
        parent.children = [...parent.children, node];
        this.expandPath(parentDraftId);
        this.notifyChange();
        return node.draftId;
    }

    renameNode(draftId: string, label: string): void {
        const node = this.findByDraftId(draftId);
        if (!node) return;
        node.label = label;
        markEdited(node);
        this.notifyChange();
    }

    toggleEnabled(draftId: string): void {
        const node = this.findByDraftId(draftId);
        if (!node) return;
        node.enabled = !node.enabled;
        markEdited(node);
        this.notifyChange();
    }

    removeNode(draftId: string): void {
        const removed = this.detach(draftId);
        if (!removed) return;
        if (removed.serverId !== null) this.removedServerIds.add(removed.serverId);
        if (this.selectedCategoryDraftId === draftId) {
            this.selectedCategoryDraftId = this.draft[0]?.draftId ?? null;
        }
        this.notifyChange();
    }

    /** Drag-drop restructure: move a node under a new parent (or to the root when null). */
    reparentNode(draftId: string, newParentDraftId: string | null): void {
        if (draftId === newParentDraftId) return;
        const node = this.findByDraftId(draftId);
        if (!node) return;
        if (newParentDraftId !== null && findNode(node.children, (n) => n.draftId === newParentDraftId) !== null) return;

        const detached = this.detach(draftId);
        if (!detached) return;
        markEdited(detached);
        const parent = newParentDraftId === null ? null : this.findByDraftId(newParentDraftId);
        if (parent === null) {
            this.draft = renumberSiblings([...this.draft, detached]);
        } else {
            parent.children = renumberSiblings([...parent.children, detached]);
            if (newParentDraftId !== null) this.expandPath(newParentDraftId);
        }
        this.notifyChange();
    }

    revert(): void {
        this.draft = this.persisted.map(cloneDraft);
        this.removedServerIds.clear();
        this.resetSuggestions();
        if (!this.draft.some((c) => c.draftId === this.selectedCategoryDraftId)) {
            this.selectedCategoryDraftId = this.draft[0]?.draftId ?? null;
        }
        this.notifyChange();
    }

    async save(): Promise<void> {
        if (this.dimensionId === null || this.saving || !this.isDirty) return;
        this.saving = true;
        this.saveError = null;
        this.notifyChange();
        try {
            await this.persistDraft();
            await this.initialize();
        } catch (cause) {
            this.saveError = cause instanceof Error ? cause.message : 'Failed to save usage categories';
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    private async persistDraft(): Promise<void> {
        for (const id of this.removedServerIds) {
            await this.taxonomy.deleteNode(id);
        }
        this.removedServerIds.clear();
        await this.createNewNodes(this.draft, null);
        await this.updateEditedNodes(this.draft, null);
        this.persisted = this.draft.map(cloneDraft);
    }

    setIncludeGeneral(value: boolean): void {
        this.includeGeneral = value;
        this.notifyChange();
    }

    get isGenerating(): boolean {
        return this.generationPhase !== 'idle';
    }

    get canGenerateFromDirectory(): boolean {
        return this.dimensionId !== null && !this.isGenerating && !this.saving && !this.loading;
    }

    /**
     * The combined generation flow: builds the grouping tree from directory
     * users (levels merged across IDPs by label — no per-provider roots),
     * OVERRIDES any prior generated subcategories under each grouping leaf, then
     * has the assist LLM fill fresh usage-task subcategories under every group.
     * Everything is staged for review — nothing persists until the user Saves.
     */
    async generateFromDirectory(): Promise<void> {
        if (!this.canGenerateFromDirectory) return;
        this.generationPhase = 'building';
        this.generationError = null;
        this.generationResult = null;
        this.notifyChange();
        try {
            const users = await this.loadAllDirectoryUsers();
            const roots = buildGroupTree(users, this.classificationLevels);
            if (roots.length === 0) {
                this.generationError = 'No directory data matched the selected grouping.';
                return;
            }

            const removed = this.removedServerIds;
            const existingGeneral = this.draft.find(isGeneralRoot) ?? null;
            const directoryDraft = this.draft.filter((n) => !isGeneralRoot(n));
            this.draft = this.reconcileGroups(roots, directoryDraft, removed);
            this.applyGeneralCatchAll(existingGeneral, removed);
            this.expanded = new Set();
            expandBranches(this.draft, this.expanded);
            if (this.selectedCategoryDraftId === null || findNode(this.draft, (n) => n.draftId === this.selectedCategoryDraftId) === null) {
                this.selectedCategoryDraftId = this.draft[0]?.draftId ?? null;
            }

            this.generationPhase = 'generating';
            const targets = this.collectSubcategoryTargets();
            this.generationProgress = { done: 0, total: targets.length };
            this.notifyChange();

            let added = 0;
            let done = 0;
            try {
                for (let start = 0; start < targets.length; start += SUBCATEGORY_CHUNK_SIZE) {
                    const chunk = targets.slice(start, start + SUBCATEGORY_CHUNK_SIZE);
                    /** Snapshot re-taken per chunk so later calls see earlier staged additions for dedup. */
                    const plan = await this.engine.generateSubcategories({ targets: chunk, taxonomy: this.snapshot() });
                    added += this.applySubcategoryResults(plan.results);
                    done += chunk.length;
                    this.generationProgress = { done, total: targets.length };
                    this.notifyChange();
                }
                added += await this.backfillShortDepartments(targets);
                const generalNote = this.includeGeneral ? ' (plus a General catch-all)' : '';
                this.generationResult = `Built ${roots.length} categories from your directory${generalNote}; generated ${added} tasks across ${done} groups.`;
            } catch (cause) {
                const message = cause instanceof Error ? cause.message : 'Could not generate subcategories';
                this.generationError = `Built the groups and generated subcategories for ${done} of ${targets.length}, then failed: ${message} Staged results are kept — review and Save, or retry.`;
            }
        } catch (cause) {
            this.generationError = cause instanceof Error ? cause.message : 'Could not generate categories from directory';
        } finally {
            this.generationPhase = 'idle';
            this.generationProgress = null;
            this.notifyChange();
        }
    }

    /**
     * The departments the LLM should fill in: childless, enabled roots (grouping
     * is a single department level). Since `reconcileGroups` clears each root's
     * prior subtree on regenerate, every department is a target — the generation
     * always overrides.
     */
    private collectSubcategoryTargets(): SubcategoryTarget[] {
        const targets: SubcategoryTarget[] = [];
        const walk = (nodes: TaxonomyDraftNode[], depth: number, pathLabels: string[]): void => {
            for (const node of nodes) {
                if (node.children.length === 0) {
                    if (node.enabled && depth <= this.classificationLevels.length - 1) {
                        targets.push({ id: node.serverId ?? node.draftId, label: node.label, pathLabels });
                    }
                    continue;
                }
                walk(node.children, depth + 1, [...pathLabels, node.label]);
            }
        };
        walk(this.draft, 0, []);
        return targets;
    }

    /**
     * Guarantee the per-department floor of task groups. The model (and Bedrock
     * tool-use, which ignores the schema's `minItems`) sometimes returns just one
     * task group for a department despite the prompt. Re-request each still-short
     * department on its own — the scoped "existing tasks" context makes the model
     * add NEW groups rather than repeat — for a bounded number of rounds. Returns
     * the count added. Best-effort: a failed round leaves the department as-is.
     */
    private async backfillShortDepartments(targets: SubcategoryTarget[]): Promise<number> {
        let added = 0;
        for (let round = 0; round < SUBCATEGORY_BACKFILL_ROUNDS; round += 1) {
            const short = targets.filter((t) => {
                const node = this.findByNodeId(t.id);
                return node !== null && node.enabled && node.children.length < MIN_TASK_GROUPS_PER_DEPARTMENT;
            });
            if (short.length === 0) break;
            for (const target of short) {
                try {
                    const plan = await this.engine.generateSubcategories({ targets: [target], taxonomy: this.snapshot() });
                    added += this.applySubcategoryResults(plan.results);
                } catch {
                    added += 0;
                }
                this.notifyChange();
            }
        }
        return added;
    }

    private applySubcategoryResults(results: SubcategoryGenerationResult[]): number {
        let added = 0;
        for (const result of results) {
            const department = this.findByNodeId(result.targetId);
            if (!department) continue;
            const groupByLabel = new Map(department.children.map((c) => [c.label.toLowerCase(), c]));
            for (const taskGroup of result.taskGroups) {
                const groupLabel = taskGroup.label.trim();
                if (groupLabel.length === 0) continue;
                let groupNode = groupByLabel.get(groupLabel.toLowerCase());
                if (groupNode === undefined) {
                    groupNode = this.newNode(groupLabel, department.children.length);
                    department.children = [...department.children, groupNode];
                    groupByLabel.set(groupLabel.toLowerCase(), groupNode);
                    added += 1;
                }
                const existingTasks = new Set(groupNode.children.map((c) => c.label.toLowerCase()));
                for (const raw of taskGroup.specificTasks) {
                    const label = raw.trim();
                    if (label.length === 0 || existingTasks.has(label.toLowerCase())) continue;
                    existingTasks.add(label.toLowerCase());
                    groupNode.children = [...groupNode.children, this.newNode(label, groupNode.children.length)];
                    added += 1;
                }
            }
            if (department.children.length > 0) this.expandPath(department.draftId);
        }
        return added;
    }

    /**
     * Merges the directory grouping into the current draft. Groups that match
     * an existing node by label keep that node — and, at the grouping leaf,
     * its whole subcategory subtree — so saved and staged usage tasks survive a
     * regenerate. Existing nodes with no matching group are staged for removal.
     */
    private reconcileGroups(target: DirectoryGroupNode[], current: TaxonomyDraftNode[], removed: Set<string>): TaxonomyDraftNode[] {
        const used = new Set<TaxonomyDraftNode>();
        const result = target.map((node, index) => {
            const match = current.find((c) => !used.has(c) && c.label.toLowerCase() === node.label.toLowerCase());
            if (match === undefined) return this.newFromTarget(node, index);
            used.add(match);
            const status = match.sortOrder === index ? match.status : 'edited';
            let children: TaxonomyDraftNode[];
            if (node.children.length === 0) {
                this.stageSubtreeRemoval(match.children, removed);
                children = [];
            } else {
                children = this.reconcileGroups(node.children, match.children, removed);
            }
            return { ...match, sortOrder: index, status, children };
        });
        for (const c of current) {
            if (!used.has(c) && c.serverId !== null) removed.add(c.serverId);
        }
        return result;
    }

    /** Stage every server-backed node in a subtree for deletion on save. */
    private stageSubtreeRemoval(nodes: TaxonomyDraftNode[], removed: Set<string>): void {
        for (const node of nodes) {
            if (node.serverId !== null) removed.add(node.serverId);
            this.stageSubtreeRemoval(node.children, removed);
        }
    }

    private newFromTarget(target: DirectoryGroupNode, sortOrder: number): TaxonomyDraftNode {
        const node = this.newNode(target.label, sortOrder);
        node.children = target.children.map((child, index) => this.newFromTarget(child, index));
        return node;
    }

    /** Re-attach (or drop) the fixed "General" catch-all after a directory
     * regenerate. Kept out of the directory reconcile so it is never staged for
     * removal or picked as an AI subcategory target (it already has children); a
     * previously-saved/edited General is preserved rather than rebuilt. */
    private applyGeneralCatchAll(existingGeneral: TaxonomyDraftNode | null, removed: Set<string>): void {
        this.draft = this.draft.filter((n) => !isGeneralRoot(n));
        if (this.includeGeneral) {
            this.draft.push(existingGeneral ?? this.buildSeedSubtree(GENERAL_DEPARTMENT, this.draft.length));
        } else if (existingGeneral) {
            this.stageSubtreeRemoval([existingGeneral], removed);
        }
    }

    /** Build a draft subtree from a bundled seed (department → group → task). */
    private buildSeedSubtree(seed: SeedNode, sortOrder: number): TaxonomyDraftNode {
        const node = this.newNode(seed.label, sortOrder);
        node.children = (seed.children ?? []).map((child, index) => this.buildSeedSubtree(child, index));
        return node;
    }

    /** Replace the working draft with the bundled default taxonomy (no directory,
     * no LLM) for review + Save — the alternative to generating from the IDP.
     * Existing saved nodes are staged for removal so Save cleanly replaces them. */
    loadDefaultSeed(): void {
        if (this.dimensionId === null || this.isGenerating || this.saving || this.loading) return;
        this.generationError = null;
        this.generationResult = null;
        this.stageSubtreeRemoval(this.draft, this.removedServerIds);
        const seeds = this.includeGeneral
            ? DEFAULT_TAXONOMY
            : DEFAULT_TAXONOMY.filter((s) => !isGeneralSeed(s));
        this.draft = seeds.map((seed, index) => this.buildSeedSubtree(seed, index));
        this.expanded = new Set();
        expandBranches(this.draft, this.expanded);
        this.selectedCategoryDraftId = this.draft[0]?.draftId ?? null;
        this.generationResult = `Loaded the default categories (${this.draft.length} departments) — review and Save.`;
        this.notifyChange();
    }

    private async loadAllDirectoryUsers(): Promise<DirectoryUserRecord[]> {
        const collected: DirectoryUserRecord[] = [];
        let page = 1;
        let totalPages = 1;
        do {
            const result = await this.directory.listUsers({ page, pageSize: 500 });
            collected.push(...result.items);
            totalPages = result.totalPages;
            page += 1;
        } while (page <= totalPages);
        return collected;
    }

    /** AI assist generates children beneath a node, so it is disallowed at the
     * leaf level (defensive — the button is also hidden there). */
    openAssist(draftId: string): void {
        if (!canAddBeneathDepth(this.nodeDepth(draftId))) return;
        this.selectedCategoryDraftId = draftId;
        this.assistModalDraftId = draftId;
        this.resetSuggestions();
        this.notifyChange();
    }

    closeAssist(): void {
        this.assistModalDraftId = null;
        this.resetSuggestions();
        this.notifyChange();
    }

    toggleSuggestion(index: number): void {
        const next = new Set(this.selectedSuggestionIndices);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        this.selectedSuggestionIndices = next;
        this.notifyChange();
    }

    setAllSuggestionsSelected(checked: boolean): void {
        this.selectedSuggestionIndices = checked ? new Set(this.suggestions.map((_, i) => i)) : new Set();
        this.notifyChange();
    }

    setSuggestionLabel(index: number, label: string): void {
        const current = this.suggestions[index];
        if (!current) return;
        if (current.kind === 'add-subcategory') this.suggestions[index] = { ...current, label };
        else if (current.kind === 'rename') this.suggestions[index] = { ...current, newLabel: label };
        else return;
        this.suggestions = [...this.suggestions];
        this.notifyChange();
    }

    setSplitSubcategory(index: number, subIndex: number, label: string): void {
        const current = this.suggestions[index];
        if (!current || current.kind !== 'split-category') return;
        const newSubcategories = current.newSubcategories.map((value, i) => (i === subIndex ? label : value));
        this.suggestions[index] = { ...current, newSubcategories };
        this.suggestions = [...this.suggestions];
        this.notifyChange();
    }

    applySelectedSuggestions(): string | null {
        let firstAddedId: string | null = null;
        this.suggestions.forEach((suggestion, index) => {
            if (!this.selectedSuggestionIndices.has(index)) return;
            const added = this.applySuggestion(suggestion);
            if (firstAddedId === null && added.length > 0) firstAddedId = added[0] ?? null;
        });
        this.assistModalDraftId = null;
        this.resetSuggestions();
        this.notifyChange();
        return firstAddedId;
    }

    async generateSuggestions(prompt: string): Promise<void> {
        const trimmed = prompt.trim();
        if (this.dimensionId === null || this.generating || trimmed.length === 0) return;
        this.generating = true;
        this.assistError = null;
        this.suggestions = [];
        this.assistNarrative = '';
        this.selectedSuggestionIndices = new Set();
        this.hasGenerated = false;
        this.notifyChange();
        try {
            const selected = this.selectedCategory;
            const plan: CategoryAssistPlan = await this.engine.suggest({
                prompt: trimmed,
                categoryId: selected ? (selected.serverId ?? selected.draftId) : undefined,
                taxonomy: this.snapshot(),
            });
            this.suggestions = plan.suggestions;
            this.assistNarrative = plan.narrative;
            this.hasGenerated = true;
        } catch (cause) {
            this.assistError = cause instanceof Error ? cause.message : 'Could not generate suggestions';
        } finally {
            this.generating = false;
            this.notifyChange();
        }
    }

    private applySuggestion(suggestion: CategorySuggestion): string[] {
        if (suggestion.kind === 'rename') {
            const node = this.findByNodeId(suggestion.targetId);
            const newLabel = suggestion.newLabel.trim();
            if (node && newLabel.length > 0) {
                node.label = newLabel;
                markEdited(node);
            }
            return [];
        }
        const parentId = suggestion.kind === 'add-subcategory' ? suggestion.parentCategoryId : suggestion.sourceCategoryId;
        const parent = this.findByNodeId(parentId);
        if (!parent) return [];
        const rawLabels = suggestion.kind === 'add-subcategory' ? [suggestion.label] : suggestion.newSubcategories;
        const labels = rawLabels.map((label) => label.trim()).filter((label) => label.length > 0);
        const addedIds: string[] = [];
        for (const label of labels) {
            const node = this.newNode(label, parent.children.length);
            parent.children = [...parent.children, node];
            addedIds.push(node.draftId);
        }
        if (addedIds.length === 0) return [];
        this.selectedCategoryDraftId = parent.draftId;
        this.expandPath(parent.draftId);
        return addedIds;
    }

    private snapshot(): TaxonomySnapshotNode[] {
        const out: TaxonomySnapshotNode[] = [];
        const walk = (nodes: TaxonomyDraftNode[], parentId: string | null): void => {
            for (const node of nodes) {
                const id = node.serverId ?? node.draftId;
                out.push({ id, label: node.label, parentId });
                walk(node.children, id);
            }
        };
        walk(this.draft, null);
        return out;
    }

    private async createNewNodes(nodes: TaxonomyDraftNode[], parentServerId: string | null): Promise<void> {
        for (const node of nodes) {
            if (node.status === 'new' && node.serverId === null && this.dimensionId !== null) {
                node.serverId = await this.taxonomy.createNode(this.dimensionId, this.toInput(node, parentServerId));
                node.status = 'unchanged';
            }
            await this.createNewNodes(node.children, node.serverId);
        }
    }

    private async updateEditedNodes(nodes: TaxonomyDraftNode[], parentServerId: string | null): Promise<void> {
        for (const node of nodes) {
            if (node.status === 'edited' && node.serverId !== null) {
                await this.taxonomy.updateNode(node.serverId, {
                    label: node.label,
                    description: node.description,
                    enabled: node.enabled,
                    sortOrder: node.sortOrder,
                    parentTaxonomyId: parentServerId,
                });
                node.status = 'unchanged';
            }
            await this.updateEditedNodes(node.children, node.serverId);
        }
    }

    private toInput(node: TaxonomyDraftNode, parentServerId: string | null): TaxonomyNodeInput {
        return {
            label: node.label,
            parentTaxonomyId: parentServerId,
            description: node.description,
            enabled: node.enabled,
            sortOrder: node.sortOrder,
        };
    }

    private newNode(label: string, sortOrder: number): TaxonomyDraftNode {
        this.draftSeq += 1;
        return {
            draftId: `new-${this.draftSeq}`,
            serverId: null,
            label,
            description: '',
            enabled: true,
            sortOrder,
            status: 'new',
            children: [],
        };
    }

    private resetSuggestions(): void {
        this.suggestions = [];
        this.assistNarrative = '';
        this.assistError = null;
        this.selectedSuggestionIndices = new Set();
        this.hasGenerated = false;
    }

    private findByDraftId(draftId: string): TaxonomyDraftNode | null {
        return findNode(this.draft, (n) => n.draftId === draftId);
    }

    /** Zero-based depth of a node in the draft tree (0 = top level). Used to
     * enforce the three-level cap on add-child and AI-assist. */
    private nodeDepth(draftId: string): number {
        const dfs = (nodes: TaxonomyDraftNode[], depth: number): number | null => {
            for (const node of nodes) {
                if (node.draftId === draftId) return depth;
                const found = dfs(node.children, depth + 1);
                if (found !== null) return found;
            }
            return null;
        };
        return dfs(this.draft, 0) ?? 0;
    }

    private findByNodeId(id: string): TaxonomyDraftNode | null {
        return findNode(this.draft, (n) => n.serverId === id || n.draftId === id);
    }

    private expandPath(targetDraftId: string): void {
        const dfs = (nodes: TaxonomyDraftNode[], trail: string[]): string[] | null => {
            for (const node of nodes) {
                const next = [...trail, node.draftId];
                if (node.draftId === targetDraftId) return next;
                const found = dfs(node.children, next);
                if (found) return found;
            }
            return null;
        };
        const path = dfs(this.draft, []);
        if (path) for (const id of path) this.expanded.add(id);
    }

    private detach(draftId: string): TaxonomyDraftNode | null {
        const removeFrom = (nodes: TaxonomyDraftNode[]): { list: TaxonomyDraftNode[]; removed: TaxonomyDraftNode | null } => {
            let removed: TaxonomyDraftNode | null = null;
            const list: TaxonomyDraftNode[] = [];
            for (const node of nodes) {
                if (node.draftId === draftId) {
                    removed = node;
                    continue;
                }
                const childResult = removeFrom(node.children);
                if (childResult.removed) {
                    node.children = childResult.list;
                    removed = childResult.removed;
                }
                list.push(node);
            }
            return { list, removed };
        };
        const result = removeFrom(this.draft);
        this.draft = result.list;
        return result.removed;
    }
}

function toDraft(node: UsageTaxonomyNode): TaxonomyDraftNode {
    return {
        draftId: node.id,
        serverId: node.id,
        label: node.label,
        description: node.description,
        enabled: node.enabled,
        sortOrder: node.sortOrder,
        status: 'unchanged',
        children: node.children.map(toDraft),
    };
}

function cloneDraft(node: TaxonomyDraftNode): TaxonomyDraftNode {
    return { ...node, children: node.children.map(cloneDraft) };
}

function markEdited(node: TaxonomyDraftNode): void {
    if (node.status === 'unchanged') node.status = 'edited';
}

function hasPendingChange(node: TaxonomyDraftNode): boolean {
    return node.status !== 'unchanged' || node.children.some(hasPendingChange);
}

function countPending(nodes: TaxonomyDraftNode[]): number {
    return nodes.reduce((sum, node) => sum + (node.status === 'unchanged' ? 0 : 1) + countPending(node.children), 0);
}

function countDisabled(nodes: TaxonomyDraftNode[]): number {
    return nodes.reduce((sum, node) => sum + (node.enabled ? 0 : 1) + countDisabled(node.children), 0);
}

function subtreeMatches(node: TaxonomyDraftNode, filter: string): boolean {
    if (node.label.toLowerCase().includes(filter)) return true;
    return node.children.some((child) => subtreeMatches(child, filter));
}

/** Departments per assist request. One at a time: batching makes the model spread
 * thin and under-fill later departments (only 1 task group), and Bedrock tool-use
 * doesn't enforce the schema's `minItems`. A single department per call gets the
 * model's full attention so it reliably honors the "≥3 task groups" instruction.
 * Slower (one call per department) but the taxonomy is generated rarely. */
const SUBCATEGORY_CHUNK_SIZE = 1;

/** Floor of task groups per department; short departments are re-requested (see
 * `backfillShortDepartments`) because the model/Bedrock tool-use won't enforce it. */
const MIN_TASK_GROUPS_PER_DEPARTMENT = 3;

/** Bounded top-up rounds over still-short departments after the main pass. */
const SUBCATEGORY_BACKFILL_ROUNDS = 2;

interface DirectoryGroupNode {
    label: string;
    children: DirectoryGroupNode[];
}

/**
 * Groups directory users by the selected dimensions with no per-provider
 * roots: the same department in two IDPs merges into one node (case-insensitive
 * label match, first-seen casing). A user whose next level extracts to null
 * simply stops contributing depth.
 */
const GENERAL_LABEL = GENERAL_DEPARTMENT.label.toLowerCase();

function isGeneralRoot(node: TaxonomyDraftNode): boolean {
    return node.label.trim().toLowerCase() === GENERAL_LABEL;
}

function isGeneralSeed(seed: SeedNode): boolean {
    return seed.label.trim().toLowerCase() === GENERAL_LABEL;
}

function buildGroupTree(users: DirectoryUserRecord[], levels: string[]): DirectoryGroupNode[] {
    const roots: DirectoryGroupNode[] = [];
    for (const user of users) {
        let siblings = roots;
        for (const key of levels) {
            const value = extractDimension(key, user, user.providerTypeId);
            if (value === null) break;
            let node = siblings.find((c) => c.label.toLowerCase() === value.toLowerCase());
            if (node === undefined) {
                node = { label: value, children: [] };
                siblings.push(node);
            }
            siblings = node.children;
        }
    }
    sortGroupTree(roots);
    return roots;
}

/** Cross-IDP arrival order is arbitrary, so alphabetize for a deterministic tree. */
function sortGroupTree(nodes: DirectoryGroupNode[]): void {
    nodes.sort((a, b) => a.label.localeCompare(b.label));
    for (const node of nodes) sortGroupTree(node.children);
}

function renumberSiblings(nodes: TaxonomyDraftNode[]): TaxonomyDraftNode[] {
    nodes.forEach((node, index) => {
        if (node.sortOrder !== index) {
            node.sortOrder = index;
            markEdited(node);
        }
    });
    return nodes;
}

function expandBranches(nodes: TaxonomyDraftNode[], into: Set<string>): void {
    for (const node of nodes) {
        if (node.children.length > 0) {
            into.add(node.draftId);
            expandBranches(node.children, into);
        }
    }
}

function findNode(nodes: TaxonomyDraftNode[], predicate: (node: TaxonomyDraftNode) => boolean): TaxonomyDraftNode | null {
    for (const node of nodes) {
        if (predicate(node)) return node;
        const found = findNode(node.children, predicate);
        if (found) return found;
    }
    return null;
}
